import { yupResolver } from "@hookform/resolvers/yup";
import {
    SearchInput,
    Popover,
    PopoverTrigger,
    PopoverContent,
    FormButton,
    Button,
    Dropdown,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
    DatePicker,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useDebounce } from "../../../../hooks/helpers";
import {
    displayNameInRightFormat,
    removeEnumUnderscore,
    makeStringFirstLetterCapital,
    handleFormatDatePickerValue,
    convertDateFilterStringToDate,
} from "../../../../utils/helpers";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { IClient } from "../../../../utils/types/client";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";
import useToast from "../../../../hooks/useToast";
import { useCreateConsent } from "../../hooks/mutations/reports.mutations";
import { useGetClientActiveSessions } from "../../hooks/queries/reports.queries";
import {
    ConsentStatus,
    DateSource,
    IClientActiveSession,
    ICreateConsentRequest,
} from "../../types/reports.types";

interface CreateConsentModalProps {
    onFormSubmit: () => void;
}

const schema = yup.object({
    client_id: yup.string().required("Client is required"),
    session_id: yup.string().required("Session is required"),
    status: yup.string().optional(),
    start_date: yup.string().optional(),
    signed_date: yup.string().optional(),
});

type FormData = yup.InferType<typeof schema>;

export default function CreateConsentForm({
    onFormSubmit,
}: CreateConsentModalProps) {
    const [clientSearchString, setClientSearchString] = React.useState("");
    const [selectedClientId, setSelectedClientId] = React.useState("");
    const [selectedClient, setSelectedClient] = React.useState<IClient | null>(
        null
    );
    const [selectedSessionId, setSelectedSessionId] = React.useState("");
    const [showClientsListPopover, setShowClientsListPopover] =
        React.useState(false);
    const [showSessionsDropdown, setShowSessionsDropdown] =
        React.useState(false);

    // Date picker states
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [signedDate, setSignedDate] = React.useState<Date | null>(null);
    const [dateSent, setDateSent] = React.useState<Date | null>(null);

    // Track whether dates were auto-calculated from session
    const [isStartDateAutoCalculated, setIsStartDateAutoCalculated] =
        React.useState(false);

    const clientsListContainerRef = React.useRef<HTMLDivElement>(null);

    const debouncedClientSearch = useDebounce(clientSearchString, 300);
    const { toast } = useToast();

    const queryClient = useQueryClient();

    const {
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            status: ConsentStatus.NOT_SENT,
        },
    });

    const clientId = watch("client_id");
    const selectedStatus = watch("status");

    const sentDateStatuses = React.useMemo(
        () => [
            ConsentStatus.SENT,
            ConsentStatus.RECEIVED,
            ConsentStatus.NEEDS_CORRECTION,
            ConsentStatus.COMPLETED,
        ],
        []
    );

    React.useEffect(() => {
        if (!sentDateStatuses.includes(selectedStatus as ConsentStatus)) {
            setDateSent(null);
        }
    }, [selectedStatus, sentDateStatuses]);

    const {
        data: clients,
        isLoading: clientsLoading,
        error: clientsError,
        isSuccess: clientsSuccess,
    } = useFetchClientList({
        page: 1,
        search_string: debouncedClientSearch,
        limit: 20,
    });

    const {
        data: sessions,
        isLoading: sessionsLoading,
        error: sessionsError,
        isSuccess: sessionsSuccess,
    } = useGetClientActiveSessions(clientId, !!clientId);

    const createConsentMutation = useCreateConsent();

    const selectedSessionDetails = sessions?.data?.find(
        (session) => session.session_id === selectedSessionId
    );

    const handleClientSelect = (client: IClient) => {
        setValue("client_id", client.client_id);
        setSelectedClientId(client.client_id);
        setSelectedClient(client);
        setClientSearchString("");
        setShowClientsListPopover(false);
        // Reset session selection when client changes
        setValue("session_id", "");
        setSelectedSessionId("");
    };

    const handleSessionSelect = (session: IClientActiveSession) => {
        setValue("session_id", session.session_id);
        setSelectedSessionId(session.session_id);
        setShowSessionsDropdown(false);

        // Auto-populate start date from session
        if (session.start_date) {
            const sessionStartDate = convertDateFilterStringToDate(
                session.start_date
            );
            setStartDate(sessionStartDate);
            setIsStartDateAutoCalculated(true);
        }
    };

    const onSubmit = async (data: FormData) => {
        const createData: ICreateConsentRequest = {
            client_id: data.client_id,
            session_id: data.session_id,
        };

        // Only include optional fields if they have values
        if (data.status) {
            createData.status = data.status as ConsentStatus;
        }
        if (startDate) {
            createData.start_date = handleFormatDatePickerValue(startDate);
            createData.start_date_source = isStartDateAutoCalculated
                ? DateSource.CALCULATED
                : DateSource.MANUAL;
        }
        if (signedDate) {
            createData.signed_date = handleFormatDatePickerValue(signedDate);
        }

        if (
            dateSent &&
            data.status &&
            sentDateStatuses.includes(data.status as ConsentStatus)
        ) {
            createData.consent_form_sent_at =
                handleFormatDatePickerValue(dateSent);
        }

        createConsentMutation.mutate(createData, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Consent created successfully",
                });
                queryClient.invalidateQueries({
                    queryKey: ["consent-reports"],
                });
                onFormSubmit();
            },
            onError: (error) => {
                toast({
                    mode: "error",
                    message:
                        error?.response?.data?.message ||
                        "Could not create consent at this time",
                });
            },
        });
    };

    const getStatusDisplay = (status: ConsentStatus): string => {
        const statusMap = {
            [ConsentStatus.NOT_SENT]: "Not Sent",
            [ConsentStatus.SENT]: "Sent",
            [ConsentStatus.RECEIVED]: "Received",
            [ConsentStatus.NEEDS_CORRECTION]: "Needs Correction",
            [ConsentStatus.COMPLETED]: "Completed",
            [ConsentStatus.CANCELLED]: "Cancelled",
            [ConsentStatus.PAUSED]: "Paused",
            [ConsentStatus.NEEDS_RENEWAL]: "Needs Renewal",
        };
        return statusMap[status] || status;
    };

    return (
        <form id="create-consent-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
                <div ref={clientsListContainerRef}>
                    <Popover
                        open={showClientsListPopover}
                        onOpenChange={(state) =>
                            setShowClientsListPopover(state)
                        }
                    >
                        <PopoverTrigger
                            asChild
                            onClick={() => setShowClientsListPopover(true)}
                        >
                            <FormButton
                                className={
                                    selectedClient
                                        ? "capitalize"
                                        : "normal-case"
                                }
                                value={
                                    selectedClient
                                        ? displayNameInRightFormat({
                                              firstName:
                                                  selectedClient.first_name,
                                              lastName:
                                                  selectedClient.last_name,
                                          })
                                        : ""
                                }
                                placeholder="Select client..."
                                label="Client"
                                isRequired
                            />
                        </PopoverTrigger>
                        <PopoverContent
                            className="popover-full"
                            align="start"
                            container={clientsListContainerRef.current}
                        >
                            <SearchInput
                                placeholder="Search for client..."
                                onChange={(e) =>
                                    setClientSearchString(e.target.value)
                                }
                                value={clientSearchString}
                            />

                            <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                {clientsLoading && (
                                    <Loader wrapperClassName="h-[150px]" />
                                )}
                                {clientsError && (
                                    <ListState
                                        isError
                                        context="general"
                                        stateHelperText="Please try again later"
                                        errorMsg={
                                            clientsError?.response?.data
                                                ?.message ||
                                            "Error loading clients"
                                        }
                                        cardClass="h-[150px]"
                                    />
                                )}
                                {clients?.data &&
                                    clientsSuccess &&
                                    clients.data.length > 0 && (
                                        <>
                                            {clients.data.map((client) => (
                                                <Button
                                                    key={client.client_id}
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                    onClick={() =>
                                                        handleClientSelect(
                                                            client
                                                        )
                                                    }
                                                >
                                                    {displayNameInRightFormat({
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    })}
                                                </Button>
                                            ))}
                                            {selectedClientId && (
                                                <Button
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                    onClick={() => {
                                                        setSelectedClientId("");
                                                        setSelectedClient(null);
                                                        setValue(
                                                            "client_id",
                                                            ""
                                                        );
                                                        setClientSearchString(
                                                            ""
                                                        );
                                                        setShowClientsListPopover(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </>
                                    )}
                            </div>
                        </PopoverContent>
                    </Popover>
                    {errors.client_id && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.client_id.message}
                        </p>
                    )}
                </div>

                {selectedClientId && (
                    <div>
                        {sessionsLoading && (
                            <Loader wrapperClassName="h-[100px]" />
                        )}
                        {sessionsError && (
                            <ListState
                                isError
                                context="general"
                                stateHelperText="Please try again later"
                                errorMsg={
                                    sessionsError?.response?.data?.message ||
                                    "Error loading sessions"
                                }
                                cardClass="h-[100px]"
                            />
                        )}
                        {sessions?.data &&
                            sessionsSuccess &&
                            (sessions.data.length > 0 ? (
                                <>
                                    <Dropdown
                                        open={showSessionsDropdown}
                                        onOpenChange={setShowSessionsDropdown}
                                    >
                                        <DropdownTrigger asChild>
                                            <FormButton
                                                value={
                                                    selectedSessionDetails
                                                        ? `${
                                                              selectedSessionDetails.provider_name
                                                          } - ${makeStringFirstLetterCapital(
                                                              removeEnumUnderscore(
                                                                  selectedSessionDetails.service
                                                              )
                                                          )} (${makeStringFirstLetterCapital(
                                                              removeEnumUnderscore(
                                                                  selectedSessionDetails.recurrence_rule
                                                              )
                                                          )})`
                                                        : ""
                                                }
                                                placeholder="Select session..."
                                                label="Session"
                                                isRequired
                                                className="normal-case"
                                                {...{
                                                    onClick: () =>
                                                        setShowSessionsDropdown(
                                                            true
                                                        ),
                                                }}
                                            />
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="start"
                                            className="max-h-[300px] overflow-y-auto"
                                        >
                                            {sessions.data.map((session) => (
                                                <DropdownItem
                                                    key={session.session_id}
                                                    onClick={() =>
                                                        handleSessionSelect(
                                                            session
                                                        )
                                                    }
                                                    className="normal-case"
                                                >
                                                    {session.provider_name} -{" "}
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            session.service
                                                        )
                                                    )}{" "}
                                                    (
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            session.recurrence_rule
                                                        )
                                                    )}
                                                    )
                                                </DropdownItem>
                                            ))}
                                        </DropdownContent>
                                    </Dropdown>
                                    {errors.session_id && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.session_id.message}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <div>
                                    <label
                                        htmlFor="no-sessions-message"
                                        className="block text-sm font-medium mb-2"
                                    >
                                        Session{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div
                                        id="no-sessions-message"
                                        className="p-3 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-600"
                                    >
                                        No active sessions available for this
                                        client
                                    </div>
                                </div>
                            ))}
                    </div>
                )}

                <div>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FormButton
                                value={
                                    selectedStatus
                                        ? getStatusDisplay(
                                              selectedStatus as ConsentStatus
                                          )
                                        : ""
                                }
                                placeholder="Select status..."
                                label="Initial Status"
                                className="normal-case"
                            />
                        </DropdownTrigger>
                        <DropdownContent align="start">
                            {Object.values(ConsentStatus).map((status) => (
                                <DropdownItem
                                    key={status}
                                    onClick={() => setValue("status", status)}
                                    isSelected={selectedStatus === status}
                                >
                                    {getStatusDisplay(status)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </div>

                {/* Dates */}
                <div>
                    <DatePicker
                        label="Start Date"
                        selected={startDate}
                        onChange={(date) => {
                            setStartDate(date);
                            setIsStartDateAutoCalculated(false);
                        }}
                        placeholderText="MM/DD/YYYY"
                        isClearable
                        isInDialog
                    />
                </div>

                {selectedStatus === ConsentStatus.COMPLETED && (
                    <div>
                        <DatePicker
                            label="Signed Date"
                            selected={signedDate}
                            onChange={(date) => setSignedDate(date)}
                            placeholderText="MM/DD/YYYY"
                            isClearable
                            isInDialog
                        />
                    </div>
                )}
                {sentDateStatuses.includes(selectedStatus as ConsentStatus) && (
                    <DatePicker
                        label="Date Sent"
                        selected={dateSent}
                        onChange={(date) => setDateSent(date)}
                        placeholderText="MM/DD/YYYY"
                        isClearable
                        isInDialog
                    />
                )}
            </div>
        </form>
    );
}
